import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SendIcon from '@mui/icons-material/Send';
import {
  EmailHistoryBatch,
  EmailHistoryRecipient,
  EmailTemplate,
  getEmailTemplateHistory,
} from '../services/emailApi';

interface EmailTemplateRowProps {
  template: EmailTemplate;
  onSend: (template: EmailTemplate) => void;
  onEdit: (template: EmailTemplate) => void;
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function statusChipColor(status: string): 'default' | 'success' | 'warning' {
  const s = (status || '').toUpperCase();
  if (s === 'ACTIVE') return 'success';
  if (s === 'DRAFT') return 'warning';
  return 'default';
}

export default function EmailTemplateRow({
  template,
  onSend,
  onEdit,
}: EmailTemplateRowProps) {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [batches, setBatches] = useState<EmailHistoryBatch[]>([]);

  const status = (template.status || '').toUpperCase();

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getEmailTemplateHistory(template.id);
      setBatches(result);
      setLoaded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load history.');
    } finally {
      setLoading(false);
    }
  }, [template.id]);

  useEffect(() => {
    if (open && !loaded && !loading) {
      void loadHistory();
    }
  }, [open, loaded, loading, loadHistory]);

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell sx={{ width: 48 }}>
          <IconButton
            aria-label={open ? 'Collapse history' : 'Expand history'}
            size="small"
            onClick={() => setOpen((prev) => !prev)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
            {template.name}
          </Typography>
        </TableCell>
        <TableCell sx={{ maxWidth: 360 }}>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {template.subject}
          </Typography>
        </TableCell>
        <TableCell>{formatDate(template.createdAt)}</TableCell>
        <TableCell>{formatDate(template.updatedAt)}</TableCell>
        <TableCell>
          <Chip
            size="small"
            label={status || 'ACTIVE'}
            color={statusChipColor(status)}
            variant={statusChipColor(status) === 'default' ? 'outlined' : 'filled'}
          />
        </TableCell>
        <TableCell align="right">
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Tooltip title="Edit template">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => onEdit(template)}
                  sx={{ textTransform: 'none' }}
                >
                  Edit
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Send to recipients">
              <span>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={() => onSend(template)}
                  sx={{
                    textTransform: 'none',
                    bgcolor: '#1976D2',
                    '&:hover': { bgcolor: '#115293' },
                  }}
                >
                  Send
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0, borderBottom: open ? undefined : 'none' }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 1 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1.5 }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Sending history
                </Typography>
                <Button
                  size="small"
                  onClick={loadHistory}
                  disabled={loading}
                  sx={{ textTransform: 'none' }}
                >
                  Refresh
                </Button>
              </Stack>

              {loading && (
                <Stack alignItems="center" sx={{ py: 3 }}>
                  <CircularProgress size={20} />
                </Stack>
              )}
              {error && (
                <Typography variant="body2" sx={{ color: 'error.main' }}>
                  {error}
                </Typography>
              )}
              {!loading && !error && batches.length === 0 && loaded && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  No sends yet for this template.
                </Typography>
              )}
              {!loading &&
                !error &&
                batches.map((batch) => (
                  <BatchHistoryCard key={batch.uuid} batch={batch} />
                ))}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

interface BatchHistoryCardProps {
  batch: EmailHistoryBatch;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50];

/**
 * Renders one batch's recipients as a paginated table. Pagination state is
 * scoped per batch so collapsing/refreshing other batches does not reset it.
 */
function BatchHistoryCard({ batch }: BatchHistoryCardProps) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(PAGE_SIZE_OPTIONS[0]);

  const recipients: EmailHistoryRecipient[] = batch.recipients;
  const total = recipients.length;
  const start = page * rowsPerPage;
  const visible = recipients.slice(start, start + rowsPerPage);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Accordion
      disableGutters
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mb: 2,
        '&:before': { display: 'none' },
        overflow: 'hidden',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          bgcolor: 'action.hover',
          px: 2,
          '& .MuiAccordionSummary-content': {
            my: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
          },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Batch sent {formatDate(batch.sentAt)}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontFamily: 'monospace' }}
          >
            {batch.uuid}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={`${batch.recipientCount} recipient${batch.recipientCount === 1 ? '' : 's'}`}
          sx={{ mr: 1 }}
        />
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>CEA number</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Purpose</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Sent at</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((r) => (
              <TableRow key={r.sentId}>
                <TableCell>{r.ceaNumber || '—'}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{r.email || '—'}</TableCell>
                <TableCell>{r.purpose || '—'}</TableCell>
                <TableCell>{formatDate(r.sentAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {total > PAGE_SIZE_OPTIONS[0] && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
}
